// Dependencies
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
// Types
import { Event } from '../objects/event';
import { Error } from '../objects/error';
import { Column } from '../objects/column';
import { EventState } from '../objects/eventState';
import { Picture } from '../../picture/objects/picture';
import { EventLocalized } from '../objects/eventLocalized';
import { Preferences } from '../../profile/objects/preferences';
import { ActivationDateError } from '../objects/activationDateError';

const setPicture = createAction<boolean | Picture>('PICTURE_SET_PICTURE');
const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const addCustomPictures = createAction<Picture[]>(
    'PICTURE_ADD_CUSTOM_PICTURES'
);

const initialState: EventState = {
    activated_accommodations: [],
    activated_module: [],
    activated_module_error: '',
    activation_date_create: false,
    activation_date_delete_index: false,
    activation_date_index: null,
    activation_dates: [],
    activation_dates_errors: [],
    address: '',
    address_search: null,
    allow_deposit: false,
    background_color: '#1B2E50',
    bank_transfer_activated: false,
    button_color: '#D6057F',
    cart_amwc: false,
    city: '',
    columns: [
        {
            label: 'name',
            field_name: 'NAME',
            checked: true,
            order_by: 'localization__name',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'event-url',
            field_name: 'EVENT_URL',
            checked: true,
            order_by: 'sub_domain',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'created-date',
            field_name: 'CREATED_DATE',
            checked: false,
            order_by: 'created_date',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'created-by',
            field_name: 'CREATED_BY',
            checked: false,
            order_by: 'creator__username',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'modified-date',
            field_name: 'MODIFIED_DATE',
            checked: true,
            order_by: 'modified_date',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'modified-by',
            field_name: 'MODIFIED_BY',
            checked: true,
            order_by: 'last_author__username',
            fixed_width: true,
            last_updated: false,
            width: null
            }
    ],
    contact_locales: {},
    count: 0,
    create: false,
    description_locales: {},
    edit: null,
    edit_activated_accommodations: false,
    edit_step: 'INFO',
    event: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_name: '',
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    footer_elements: [],
    google_id: null,
    invoice_prefix: '',
    invoice_prefix_error: '',
    is_password_protected: false,
    is_silent_sign_in: false,
    latitude: '',
    limit: 50,
    list: null,
    logo: null,
    logo_errors: null,
    logo_progress: 0,
    longitude: '',
    name_locales: {},
    opened_btc: false,
    no_invoice_on_cb_payment: false,
    order: 'asc',
    order_by: 'name',
    page: 0,
    password: '',
    pictures: [],
    preferences: [],
    refresh: false,
    running: false,
    search: '',
    sub_domain: '',
    sub_domain_error: '',
    terms_and_conditions_locales: {},
    text_color: '#1B2E50',
    title_locales: {},
    traveler_can_add: true,
    traveler_can_delete: false,
    unavailable_message_locales: {},
    zip_code: '',
    agent: null,
    meeting_code: '',
    allowed_users: [],
    allowed_teams: [],
    client_list: [],
    client_list_error: undefined,
    accounting_code: '',
    analytic_codes: [],
    cart_awmc: false
};

const updateActivationProperty = (
    value: any,
    index: number,
    state: EventState,
    key: string
) => {
    const activation_dates = [...state.activation_dates];
    const activation_dates_errors = [...state.activation_dates_errors];
    const activation_date = { ...activation_dates[index] };
    const activation_date_error = { ...activation_dates_errors[index] };
    activation_date[key] = value;
    if(key !== 'name'){
        activation_date_error[key as unknown as keyof ActivationDateError] = '';
    }
    activation_dates[index] = activation_date;
    activation_dates_errors[index] = activation_date_error;
    state.activation_dates = activation_dates;
    state.activation_dates_errors = activation_dates_errors;
};

export const eventSlice = createSlice({
    name: 'event',
    initialState,
    reducers: {
        addActivationDate: (state) => {
            const activation_dates = [...state.activation_dates];
            const activation_dates_errors = [...state.activation_dates_errors];
            activation_dates.push({
                name: '',
                bank_transfer_limit_date: null,
                cancellation_limit_date: null,
                deposit_limit_date: null,
                start_date: null,
                end_date: null,
                event_start_date: null,
                event_end_date: null,
                pax_update_limit_date: null,
                nb_days_allowed_after_event: 3,
                nb_days_allowed_before_event: 3
            });
            activation_dates_errors.push({
                bank_transfer_limit_date: '',
                cancellation_limit_date: '',
                deposit_limit_date: '',
                start_date: '',
                end_date: '',
                event_start_date: '',
                event_end_date: '',
                pax_update_limit_date: '',
                nb_days_allowed_after_event: '',
                nb_days_allowed_before_event: ''
            });
            state.activation_dates = activation_dates;
            state.activation_dates_errors = activation_dates_errors;
        },
        addPreferences: (state, action) => {
            state.preferences = [...state.preferences, action.payload];
        },
        deleteActivationDate: (state, action) => {
            const activation_dates = [...state.activation_dates];
            const activation_dates_errors = [...state.activation_dates_errors];
            activation_dates.splice(action.payload, 1);
            activation_dates_errors.splice(action.payload, 1);
            state.activation_dates = activation_dates;
            state.activation_dates_errors = activation_dates_errors;
        },
        deletePicture: (state, action) => {
            const pictures = [...state.pictures];
            pictures.splice(action.payload, 1);
            state.pictures = pictures;
        },
        moveDownColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetEvent: (state) => {
            state.create = false;
            state.activated_accommodations = [];
            state.activated_module = [];
            state.activated_module_error = '';
            state.activation_date_create = false;
            state.activation_date_delete_index = false;
            state.activation_date_index = null;
            state.activation_dates = [];
            state.activation_dates_errors = [];
            state.address = '';
            state.address_search = null;
            state.allow_deposit = false;
            state.background_color = '#1B2E50';
            state.bank_transfer_activated = false;
            state.button_color = '#D6057F';
            state.city = '';
            state.contact_locales = {};
            state.description_locales = {};
            state.edit = null;
            state.edit_activated_accommodations = false;
            state.edit_step = 'INFO';
            state.event = null;
            state.footer_elements = [];
            state.google_id = null;
            state.invoice_prefix = '';
            state.invoice_prefix_error = '';
            state.is_silent_sign_in = false;
            state.is_password_protected = false;
            state.password = '';
            state.latitude = '';
            state.logo = null;
            state.logo_errors = null;
            state.logo_progress = 0;
            state.longitude = '';
            state.name_locales = {};
            state.opened_btc = false;
            state.no_invoice_on_cb_payment = false;
            state.pictures = [];
            state.sub_domain = '';
            state.sub_domain_error = '';
            state.terms_and_conditions_locales = {};
            state.text_color = '#1B2E50';
            state.title_locales = {};
            state.traveler_can_add = true;
            state.traveler_can_delete = false;
            state.unavailable_message_locales = {};
            state.zip_code = '';
            state.meeting_code = '';
            state.allowed_users = [];
            state.allowed_teams = [];
            state.client_list = [];
            state.client_list_error = undefined;
            state.accounting_code = '';
            state.analytic_codes = [];
            state.agent = null;
            state.refresh = !state.refresh;
            state.cart_amwc = false;
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_last_author = '';
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_name = '';
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
        },
        setEdit: (state, action) => {
            if (state.event !== null) {
                const contact_locales: Record<string, string> = {};
                const description_locales: Record<string, string> = {};
                const name_locales: Record<string, string> = {};
                const title_locales: Record<string, string> = {};
                const unavailable_message_locales: Record<string, string> = {};
                const terms_and_conditions_locales: Record<string, string> = {};
                let has_french = false;
                state.event.localization.map((locale: EventLocalized) => {
                    if (locale.locale === 1) {
                        has_french = true;
                    }
                    contact_locales[locale.locale] = locale.contact_info;
                    description_locales[locale.locale] = locale.description;
                    name_locales[locale.locale] = locale.name;
                    title_locales[locale.locale] = locale.title;
                    terms_and_conditions_locales[locale.locale] =
                        locale.terms_and_conditions;
                    unavailable_message_locales[locale.locale] = locale.unavailable_message || '';
                });
                if (!has_french) {
                    contact_locales['1'] = '';
                    description_locales['1'] =
                        state.event.description !== null
                            ? state.event.description
                            : '';
                    name_locales['1'] =
                        state.event.name !== null ? state.event.name : '';
                    title_locales['1'] =
                        state.event.title !== null ? state.event.title : '';
                    terms_and_conditions_locales['1'] = '';
                    unavailable_message_locales['1'] = '';
                }
                const place = state.event.place;
                state.edit = action.payload;
                state.activated_accommodations = [
                    ...state.event.allowed_hotels
                ];
                state.activated_module = [...(state.event.activated_module ?? [])];
                state.activation_date_create = false;
                state.activation_date_delete_index = false;
                state.activation_date_index = null;
                state.activation_dates = [...state.event.activation_dates].map((activation_date) => ({
                    deposit_limit_date: activation_date.deposit_limit_date? new Date(activation_date.deposit_limit_date) : null,
                    cancellation_limit_date: activation_date.cancellation_limit_date? new Date(activation_date.cancellation_limit_date) : null,
                    start_date: activation_date.start_date? new Date(activation_date.start_date) : null,
                    end_date: activation_date.end_date? new Date(activation_date.end_date) : null,
                    event_start_date: activation_date.event_start_date? new Date(activation_date.event_start_date) : null,
                    event_end_date: activation_date.event_end_date? new Date(activation_date.event_end_date) : null,
                    pax_update_limit_date: activation_date.pax_update_limit_date? new Date(activation_date.pax_update_limit_date) : null,
                    bank_transfer_limit_date: activation_date.bank_transfer_limit_date? new Date(activation_date.bank_transfer_limit_date) : null,
                    name: activation_date.name ?? '',
                    id: activation_date.id ?? undefined,
                    nb_days_allowed_after_event: activation_date.nb_days_allowed_after_event ?? '',
                    nb_days_allowed_before_event: activation_date.nb_days_allowed_before_event ?? ''
                }));
                state.activation_dates_errors = [...state.event.activation_dates].map(() => ({
                    bank_transfer_limit_date: '',
                    cancellation_limit_date: '',
                    deposit_limit_date: '',
                    start_date: '',
                    end_date: '',
                    event_start_date: '',
                    event_end_date: '',
                    pax_update_limit_date: '',
                    nb_days_allowed_after_event: '',
                    nb_days_allowed_before_event: ''
                }));
                state.address =
                    place !== null && place.address !== null
                        ? place.address
                        : '';
                state.address_search = null;
                state.allow_deposit = state.event.allow_deposit;
                state.bank_transfer_activated = state.event.bank_transfer_activated;
                state.opened_btc = state.event.opened_btc;
                state.no_invoice_on_cb_payment = state.event.no_invoice_on_cb_payment;
                state.background_color = state.event.background_color || '#1B2E50';
                state.button_color = state.event.button_color || '#D6057F';
                state.city =
                    place !== null && place.city_name !== null
                        ? place.city_name
                        : '';
                state.contact_locales = contact_locales;
                state.description_locales = description_locales;
                state.edit_activated_accommodations = false;
                state.footer_elements = state.event.footer_elements ?? [];
                state.google_id =
                    place !== null && place.google_id !== null
                        ? place.google_id
                        : '';
                state.invoice_prefix = state.event.invoice_prefix;
                state.invoice_prefix_error = '';
                state.is_silent_sign_in = state.event.is_silent_signin;
                state.is_password_protected = state.event.event_password !== null;
                state.password = state.event.event_password !== null ? state.event.event_password : '';
                state.latitude =
                    place !== null && place.latitude !== null
                        ? place.latitude
                        : '';
                state.logo = state.event.logo;
                state.logo_errors = null;
                state.logo_progress = 0;
                state.longitude =
                    place !== null && place.longitude !== null
                        ? place.longitude
                        : '';
                state.name_locales = name_locales;
                state.pictures = [...state.event.pictures];
                state.sub_domain = state.event.sub_domain;
                state.sub_domain_error = '';
                state.terms_and_conditions_locales =
                    terms_and_conditions_locales;
                state.text_color = state.event.text_color || '#1B2E50';
                state.title_locales = title_locales;
                state.traveler_can_add = state.event.traveler_can_add;
                state.traveler_can_delete = state.event.traveler_can_delete;
                state.unavailable_message_locales = unavailable_message_locales;
                state.zip_code =
                    place !== null && place.zip_code !== null
                        ? place.zip_code
                        : '';
                state.meeting_code = state.event.meeting_code ?? '';
                state.allowed_users = state.event.allowed_users ?? [];
                state.allowed_teams = state.event.allowed_teams ?? [];
                state.client_list = state.event.client_list ?? [];
                state.client_list_error = undefined;
                state.accounting_code = state.event.accounting_code ?? '';
                state.analytic_codes = state.event.analytic_codes ?? [];
                state.agent = state.event.agent;
                state.cart_amwc = state.event.cart_amwc;
            } else {
                state.edit = action.payload;
            }
        },
        setEditStep: (state, action) => {
            state.edit_step = action.payload;
        },
        setEvent: (state, action: PayloadAction<Event>) => {
            const contact_locales: Record<string, string> = {};
            const description_locales: Record<string, string> = {};
            const name_locales: Record<string, string> = {};
            const title_locales: Record<string, string> = {};
            const terms_and_conditions_locales: Record<string, string> = {};
            const unavailable_message_locales: Record<string, string> = {};
            let has_french = false;
            action.payload.localization.map((locale: EventLocalized) => {
                if (locale.locale === 1) {
                    has_french = true;
                }
                contact_locales[locale.locale] = locale.contact_info;
                description_locales[locale.locale] = locale.description;
                name_locales[locale.locale] = locale.name;
                title_locales[locale.locale] = locale.title;
                terms_and_conditions_locales[locale.locale] =
                    locale.terms_and_conditions;
                unavailable_message_locales[locale.locale] = locale.unavailable_message || '';
            });
            if (!has_french) {
                contact_locales['1'] = '';
                description_locales['1'] =
                    action.payload.name !== null ? action.payload.name : '';
                name_locales['1'] =
                    action.payload.description !== null
                        ? action.payload.description
                        : '';
                title_locales['1'] =
                    action.payload.title !== null ? action.payload.title : '';
                terms_and_conditions_locales['1'] = '';
                unavailable_message_locales['1'] = '';
            }
            const place = action.payload.place;
            state.edit = null;
            state.event = action.payload;
            state.activated_accommodations = [...action.payload.allowed_hotels];
            state.activated_module = [...(action.payload.activated_module ?? [])];
            state.activation_date_create = false;
            state.activation_date_delete_index = false;
            state.activation_date_index = null;
            state.activation_dates = [...action.payload.activation_dates].map((activation_date) => ({
                ...activation_date,
                start_date: activation_date.start_date? new Date(activation_date.start_date): null,
                end_date: activation_date.end_date? new Date(activation_date.end_date): null,
                event_start_date: activation_date.event_start_date? new Date(activation_date.event_start_date): null,
                event_end_date: activation_date.event_end_date? new Date(activation_date.event_end_date): null,
                cancellation_limit_date: activation_date.cancellation_limit_date? new Date(activation_date.cancellation_limit_date): null,
                pax_update_limit_date: activation_date.pax_update_limit_date? new Date(activation_date.pax_update_limit_date): null,
                deposit_limit_date: activation_date.deposit_limit_date? new Date(activation_date.deposit_limit_date): null,
                bank_transfer_limit_date: activation_date.bank_transfer_limit_date? new Date(activation_date.bank_transfer_limit_date): null,
                nb_days_allowed_after_event: activation_date.nb_days_allowed_after_event ?? '',
                nb_days_allowed_before_event: activation_date.nb_days_allowed_before_event ?? ''
            }));
            state.activation_dates_errors = [...action.payload.activation_dates].map((_) => ({
                bank_transfer_limit_date: '',
                cancellation_limit_date: '',
                deposit_limit_date: '',
                start_date: '',
                end_date: '',
                event_start_date: '',
                event_end_date: '',
                pax_update_limit_date: '',
                nb_days_allowed_after_event: '',
                nb_days_allowed_before_event: ''
            }));
            state.address =
                place !== null && place.address !== null ? place.address : '';
            state.address_search = null;
            state.allow_deposit = action.payload.allow_deposit;
            state.bank_transfer_activated = action.payload.bank_transfer_activated;
            state.opened_btc = action.payload.opened_btc;
            state.no_invoice_on_cb_payment = action.payload.no_invoice_on_cb_payment;
            state.background_color = action.payload.background_color || '#1B2E50';
            state.button_color = action.payload.button_color || '#D6057F';
            state.city =
                place !== null && place.city_name !== null
                    ? place.city_name
                    : '';
            state.contact_locales = contact_locales;
            state.description_locales = description_locales;
            state.edit_activated_accommodations = false;
            state.footer_elements = action.payload.footer_elements ?? [];
            state.google_id =
                place !== null && place.google_id !== null
                    ? place.google_id
                    : '';
            state.invoice_prefix = action.payload.invoice_prefix;
            state.invoice_prefix_error = '';
            state.is_silent_sign_in = action.payload.is_silent_signin;
            state.is_password_protected = action.payload.event_password !== null;
            state.password = action.payload.event_password !== null ? action.payload.event_password : '';
            state.latitude =
                place !== null && place.latitude !== null ? place.latitude : '';
            state.logo = action.payload.logo;
            state.logo_errors = null;
            state.logo_progress = 0;
            state.longitude =
                place !== null && place.longitude !== null
                    ? place.longitude
                    : '';
            state.name_locales = name_locales;
            state.pictures = [...action.payload.pictures];
            state.sub_domain = action.payload.sub_domain;
            state.sub_domain_error = '';
            state.terms_and_conditions_locales = terms_and_conditions_locales;
            state.text_color = action.payload.text_color || '#1B2E50';
            state.title_locales = title_locales;
            state.traveler_can_add = action.payload.traveler_can_add;
            state.traveler_can_delete = action.payload.traveler_can_delete;
            state.unavailable_message_locales = unavailable_message_locales;
            state.zip_code =
                place !== null && place.zip_code !== null ? place.zip_code : '';
            state.meeting_code = action.payload.meeting_code ?? '';
            state.allowed_users = action.payload.allowed_users ?? [];
            state.allowed_teams = action.payload.allowed_teams ?? [];
            state.agent = action.payload.agent;
            state.client_list = action.payload.client_list ?? [];
            state.client_list_error = undefined;
            state.accounting_code = action.payload.accounting_code ?? '';
            state.analytic_codes = action.payload.analytic_codes ?? [];
            state.agent = action.payload.agent;
            state.cart_amwc = action.payload.cart_amwc;
        },
        setEventActivationDate: (state, action) => {
            if (state.event !== null) {
                if (action.payload === true) {
                    state.activation_date_index =
                        state.event.activation_dates.length;
                    state.activation_date_create = true;
                    state.activation_dates = [
                        ...state.event.activation_dates.map((activation_date) => ({
                            deposit_limit_date: activation_date.deposit_limit_date? new Date(activation_date.deposit_limit_date) : null,
                            cancellation_limit_date: activation_date.cancellation_limit_date? new Date(activation_date.cancellation_limit_date) : null,
                            start_date: activation_date.start_date? new Date(activation_date.start_date) : null,
                            end_date: activation_date.end_date? new Date(activation_date.end_date) : null,
                            event_start_date: activation_date.event_start_date? new Date(activation_date.event_start_date) : null,
                            event_end_date: activation_date.event_end_date? new Date(activation_date.event_end_date) : null,
                            pax_update_limit_date: activation_date.pax_update_limit_date? new Date(activation_date.pax_update_limit_date) : null,
                            bank_transfer_limit_date: activation_date.bank_transfer_limit_date? new Date(activation_date.bank_transfer_limit_date) : null,
                            name: activation_date.name ?? '',
                            id: activation_date.id ?? undefined,
                            nb_days_allowed_after_event: activation_date.nb_days_allowed_after_event ?? '',
                            nb_days_allowed_before_event: activation_date.nb_days_allowed_before_event ?? ''
                        })),
                        {
                            name: '',
                            bank_transfer_limit_date: null,
                            cancellation_limit_date: null,
                            deposit_limit_date: null,
                            start_date: null,
                            end_date: null,
                            event_start_date: null,
                            event_end_date: null,
                            pax_update_limit_date: null,
                            nb_days_allowed_after_event: 3,
                            nb_days_allowed_before_event: 3
                        }
                    ];
                    state.activation_dates_errors = [
                        ...state.event.activation_dates.map(() => ({
                            bank_transfer_limit_date: '',
                            cancellation_limit_date: '',
                            deposit_limit_date: '',
                            start_date: '',
                            end_date: '',
                            event_start_date: '',
                            event_end_date: '',
                            pax_update_limit_date: '',
                            nb_days_allowed_after_event: '',
                            nb_days_allowed_before_event: ''
                        })),
                        {
                            bank_transfer_limit_date: '',
                            cancellation_limit_date: '',
                            deposit_limit_date: '',
                            start_date: '',
                            end_date: '',
                            event_start_date: '',
                            event_end_date: '',
                            pax_update_limit_date: '',
                            nb_days_allowed_after_event: '',
                            nb_days_allowed_before_event: ''
                        }
                    ];
                } else if (action.payload === false) {
                    state.activation_date_index = null;
                    state.activation_date_create = false;
                    state.activation_dates = [...state.event.activation_dates].map((activation_date) => ({
                        deposit_limit_date: activation_date.deposit_limit_date? new Date(activation_date.deposit_limit_date) : null,
                        cancellation_limit_date: activation_date.cancellation_limit_date? new Date(activation_date.cancellation_limit_date) : null,
                        start_date: activation_date.start_date? new Date(activation_date.start_date) : null,
                        end_date: activation_date.end_date? new Date(activation_date.end_date) : null,
                        event_start_date: activation_date.event_start_date? new Date(activation_date.event_start_date) : null,
                        event_end_date: activation_date.event_end_date? new Date(activation_date.event_end_date) : null,
                        pax_update_limit_date: activation_date.pax_update_limit_date? new Date(activation_date.pax_update_limit_date) : null,
                        bank_transfer_limit_date: activation_date.bank_transfer_limit_date? new Date(activation_date.bank_transfer_limit_date) : null,
                        name: activation_date.name ?? '',
                        id: activation_date.id ?? undefined,
                        nb_days_allowed_after_event: activation_date.nb_days_allowed_after_event ?? '',
                        nb_days_allowed_before_event: activation_date.nb_days_allowed_before_event ?? ''
                    }));
                    state.activation_dates_errors = [...state.event.activation_dates].map(() => ({
                        bank_transfer_limit_date: '',
                        cancellation_limit_date: '',
                        deposit_limit_date: '',
                        start_date: '',
                        end_date: '',
                        event_start_date: '',
                        event_end_date: '',
                        pax_update_limit_date: '',
                        nb_days_allowed_after_event: '',
                        nb_days_allowed_before_event: ''
                    }));
                } else {
                    state.activation_date_index = action.payload;
                    state.activation_date_create = false;
                    state.activation_dates = [...state.event.activation_dates].map((activation_date) => ({
                        deposit_limit_date: activation_date.deposit_limit_date? new Date(activation_date.deposit_limit_date) : null,
                        cancellation_limit_date: activation_date.cancellation_limit_date? new Date(activation_date.cancellation_limit_date) : null,
                        start_date: activation_date.start_date? new Date(activation_date.start_date) : null,
                        end_date: activation_date.end_date? new Date(activation_date.end_date) : null,
                        event_start_date: activation_date.event_start_date? new Date(activation_date.event_start_date) : null,
                        event_end_date: activation_date.event_end_date? new Date(activation_date.event_end_date) : null,
                        pax_update_limit_date: activation_date.pax_update_limit_date? new Date(activation_date.pax_update_limit_date) : null,
                        bank_transfer_limit_date: activation_date.bank_transfer_limit_date? new Date(activation_date.bank_transfer_limit_date) : null,
                        name: activation_date.name ?? '',
                        id: activation_date.id ?? undefined,
                        nb_days_allowed_after_event: activation_date.nb_days_allowed_after_event ?? '',
                        nb_days_allowed_before_event: activation_date.nb_days_allowed_before_event ?? ''
                    }));
                    state.activation_dates_errors = [...state.event.activation_dates].map(() => ({
                        bank_transfer_limit_date: '',
                        cancellation_limit_date: '',
                        deposit_limit_date: '',
                        start_date: '',
                        end_date: '',
                        event_start_date: '',
                        event_end_date: '',
                        pax_update_limit_date: '',
                        nb_days_allowed_after_event: '',
                        nb_days_allowed_before_event: ''
                    }));
                }
            }
        },
        setEventActivationDateDelete: (state, action) => {
            state.activation_date_delete_index = action.payload;
        },
        setErrors: (state, action) => {
            const errors = action.payload;
            const new_state = { ...state };
            errors.map((error: Error) => {
                if (error.subkey !== undefined && error.subkey_index !== undefined) {
                    const property = [...new_state[error.key]];
                    const sub_property = { ...property[error.subkey_index] };
                    sub_property[error.subkey] = error.error;
                    property[error.subkey_index] = sub_property;
                    new_state[error.key] = property;
                } else {
                    new_state[error.key] = error.error;
                }
            });
            return { ...new_state };
        },
        setEventPreferences: (state, action) => {
            state.preferences = action.payload;
        },
        setExportType: (state, action) => {
            state.export_type = action.payload;
        },
        setLimit: (state, action) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setLogoErrors: (state, action) => {
            state.logo_progress = 0;
            state.logo_errors = action.payload;
        },
        setOrderBy: (state, action) => {
            state.list = null;
            state.order_by = action.payload;
            state.order =
                state.order_by === action.payload && state.order === 'asc'
                    ? 'desc'
                    : 'asc';
        },
        setPage: (state, action) => {
            state.list = null;
            state.page = action.payload;
        },
        setRunning: (state, action) => {
            state.running = action.payload;
        },
        setSearch: (state, action) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        toggleAllowDeposit: (state) => {
            state.allow_deposit = !state.allow_deposit;
        },
        toggleBankTransferActivated: ((state) => ({ ...state, bank_transfer_activated: !state.bank_transfer_activated })),
        toggleOpentoBTC: (state) => ({ ...state, opened_btc: !state.opened_btc }),
        toggleNoInvoiceOnCbPayment: (state) => ({ ...state, no_invoice_on_cb_payment: !state.no_invoice_on_cb_payment }),
        toggleCartAMWC: (state) => ({ ...state, cart_amwc: !state.cart_amwc }),
        toggleColumn: (state, action) => {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleCreate: (state) => {
            state.create = !state.create;
        },
        toggleEditActivatedAccommodations: (state) => {
            if (state.event !== null) {
                state.edit_activated_accommodations =
                    !state.edit_activated_accommodations;
                state.activated_accommodations = state.event.allowed_hotels;
            }
        },
        toggleExport: (state, action) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        toggleHasPassword: (state) => {
            state.is_password_protected = !state.is_password_protected;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        toggleSilentSignIn: (state) => {
            state.is_silent_sign_in = !state.is_silent_sign_in;
        },
        toggleTravelerCanAdd: (state) => {
            state.traveler_can_add = !state.traveler_can_add;
        },
        toggleTravelerCanDelete: (state) => {
            state.traveler_can_delete = !state.traveler_can_delete;
        },
        updateActivatedAccommodations: (state, action) => {
            state.activated_accommodations = action.payload;
        },
        updateActivatedModule: (state, action) => {
            state.activated_module = action.payload;
            state.activated_module_error = '';
        },
        updateActivationBankTransferLimitDate: (state, action) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(date, date_index, state, 'bank_transfer_limit_date');
        },
        updateActivationCancellationLimitDate: (
            state,
            action: PayloadAction<{ date: null | Date; date_index: number }>
        ) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(
                date,
                date_index,
                state,
                'cancellation_limit_date'
            );
        },
        updateActivationDepositLimitDate: (
            state,
            action: PayloadAction<{ date: null | Date; date_index: number }>
        ) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(
                date,
                date_index,
                state,
                'deposit_limit_date'
            );
        },
        updateActivationPaxUpdateLimitDate: (
            state,
            action: PayloadAction<{ date: null | Date; date_index: number }>
        ) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(
                date,
                date_index,
                state,
                'pax_update_limit_date'
            );
        },
        updateActivationEndDate: (
            state,
            action: PayloadAction<{ date: null | Date; date_index: number }>
        ) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(date, date_index, state, 'end_date');
        },
        updateActivationEventEndDate: (
            state,
            action: PayloadAction<{ date: null | Date; date_index: number }>
        ) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(date, date_index, state, 'event_end_date');
        },
        updateActivationEventStartDate: (
            state,
            action: PayloadAction<{ date: null | Date; date_index: number }>
        ) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(
                date,
                date_index,
                state,
                'event_start_date'
            );
        },
        updateActivationName: (
            state,
            action: PayloadAction<{ name: string; date_index: number }>
        ) => {
            const { name, date_index } = action.payload;
            updateActivationProperty(name, date_index, state, 'name');
        },
        updateActivationNbDaysAllowedAfterEvent: (
            state,
            action: PayloadAction<{ nb_days_allowed_after_event: string | number; date_index: number }>
        ) => {
            const { nb_days_allowed_after_event, date_index } = action.payload;
            updateActivationProperty(nb_days_allowed_after_event, date_index, state, 'nb_days_allowed_after_event');
        },
        updateActivationNbDaysAllowedBeforeEvent: (
            state,
            action: PayloadAction<{ nb_days_allowed_before_event: string | number; date_index: number }>
        ) => {
            const { nb_days_allowed_before_event, date_index } = action.payload;
            updateActivationProperty(nb_days_allowed_before_event, date_index, state, 'nb_days_allowed_before_event');
        },
        updateActivationStartDate: (
            state,
            action: PayloadAction<{ date: null | Date; date_index: number }>
        ) => {
            const { date, date_index } = action.payload;
            updateActivationProperty(date, date_index, state, 'start_date');
        },
        updateAddress: (state, action) => {
            state.address = action.payload;
        },
        updateAddressSearch: (state, action) => {
            state.address_search = action.payload;
            state.google_id = action.payload.place_id;
        },
        updateBackgroundColor: (state, action) => {
            state.background_color = action.payload;
        },
        updateButtonColor: (state, action) => {
            state.button_color = action.payload;
        },
        updateCity: (state, action) => {
            state.city = action.payload;
        },
        updateContactLocale: (
            state,
            action: PayloadAction<{ id: number; value: string }>
        ) => {
            const { id, value } = action.payload;
            const contact_locales = { ...state.contact_locales };
            contact_locales[id] = value;
            state.contact_locales = contact_locales;
        },
        updateDescriptionLocale: (
            state,
            action: PayloadAction<{ id: number; value: string }>
        ) => {
            const { id, value } = action.payload;
            const description_locales = { ...state.description_locales };
            description_locales[id] = value;
            state.description_locales = description_locales;
        },
        updateFilterCreatedDateGte: (state, action) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreator: (state, action) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action) => {
            state.filter_creator = action.payload;
        },
        updateFilterLastAuthor: (state, action) => {
            state.filter_last_author = action.payload;
        },
        updateFilterModifiedDateGte: (state, action) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterName: (state, action) => {
            state.filter_name = action.payload;
        },
        updateFilterPeriod: (state, action) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte =
                action.payload === 'MODIFICATION'
                    ? null
                    : state.filter_created_date_gte;
            state.filter_created_date_lte =
                action.payload === 'MODIFICATION'
                    ? null
                    : state.filter_created_date_lte;
            state.filter_modified_date_gte =
                action.payload === 'CREATION'
                    ? null
                    : state.filter_modified_date_gte;
            state.filter_modified_date_lte =
                action.payload === 'CREATION'
                    ? null
                    : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action) => {
            state.filter_period_type = action.payload;
        },
        updateFooterElements: (state, action) => {
            state.footer_elements = action.payload;
        },
        updateGoogleId: (state, action) => {
            state.google_id = action.payload;
        },
        updateInvoicePrefix: (state, action) => {
            state.invoice_prefix = action.payload;
        },
        updateInvoicePrefixError: (state, action) => {
            state.invoice_prefix_error = action.payload;
        },
        updateLatitude: (state, action) => {
            state.latitude = action.payload;
        },
        updateLongitude: (state, action) => {
            state.longitude = action.payload;
        },
        updateLogo: (state, action) => {
            state.logo_progress = 0;
            state.logo = action.payload;
        },
        updateLogoProgress: (state, action) => {
            state.logo_progress = action.payload;
        },
        updatePictureOrder: (state, action) => {
            const { drag_index, hover_index } = action.payload;
            const pictures = [...state.pictures];
            pictures.splice(drag_index, 1);
            pictures.splice(hover_index, 0, state.pictures[drag_index]);
            state.pictures = pictures;
        },
        updateNameLocale: (
            state,
            action: PayloadAction<{ id: number; value: string }>
        ) => {
            const { id, value } = action.payload;
            const name_locales = { ...state.name_locales };
            name_locales[id] = value;
            state.name_locales = name_locales;
        },
        updatePassword: (
            state,
            action: PayloadAction<string>
        ) => {
            state.password = action.payload;
        },
        updateSubDomain: (state, action) => {
            state.sub_domain = action.payload;
            state.sub_domain_error = '';
        },
        updateTermsAndConditionsLocale: (
            state,
            action: PayloadAction<{ id: number; value: string }>
        ) => {
            const { id, value } = action.payload;
            const terms_and_conditions_locales = {
                ...state.terms_and_conditions_locales
            };
            console.log('id : ', id);
            console.log('value : ', value);
            terms_and_conditions_locales[id] = value;
            console.log('terms_and_conditions_locales : ', terms_and_conditions_locales);
            state.terms_and_conditions_locales = { ...terms_and_conditions_locales };
        },
        updateTextColor: (state, action) => {
            state.text_color = action.payload;
        },
        updateTitleLocale: (
            state,
            action: PayloadAction<{ id: number; value: string }>
        ) => {
            const { id, value } = action.payload;
            const title_locales = { ...state.title_locales };
            title_locales[id] = value;
            state.title_locales = title_locales;
        },
        updateUnavailableMessageLocale: (state, action: PayloadAction<{ id: number; value: string }>) => {
            const { id, value } = action.payload;
            const unavailable_message_locales = {
                ...state.unavailable_message_locales
            };
            console.log('id : ', id);
            console.log('value : ', value);
            unavailable_message_locales[id] = value;
            state.unavailable_message_locales = { ...unavailable_message_locales };
        },
        updateZipCode: (state, action) => {
            state.zip_code = action.payload;
        },
        updateMeetingCode: (state, action) => {
            state.meeting_code = action.payload;
        },
        updateAllowedUsers: (state, action) => {
            state.allowed_users = action.payload;
        },
        updateAllowedTeams: (state, action) => {
            state.allowed_teams = action.payload;
        },
        updateClientList: (state, action) => {
            state.client_list = action.payload;
            state.client_list_error = undefined;
        },
        updateAccountingCode: (state, action) => {
            state.accounting_code = action.payload;
        },
        updateAnalyticCode: (state, action) => {
            const { index, value } = action.payload;
            state.analytic_codes[index] = value;
        },
        deleteAnalyticCode: (state, action) => {
            state.analytic_codes = state.analytic_codes.filter((_, index) => index !== action.payload);
        },
        addAnalyticCode: (state) => {
            state.analytic_codes = [...state.analytic_codes, { product_type: null, analytic_code: '' } ];
        },
        updateAgent: (state, action) => {
            state.agent = action.payload;
        },
        setColumns: (state, action) => {
            state.columns = action.payload;
        },
        resizeColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.columns = applyColumnResize(state.columns, action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(addCustomPictures, (state, action) => {
                if (state.event !== null || state.create) {
                    state.pictures = [...state.pictures, ...action.payload];
                }
            })
            .addCase(setPicture, (state, action) => {
                if (action.payload !== true && action.payload !== false) {
                    const pictures = [...state.pictures];
                    for (let i = 0; i < pictures.length; i++) {
                        if (pictures[i].id === action.payload.id) {
                            pictures[i] = { ...action.payload };
                            break;
                        }
                    }
                    state.pictures = pictures;
                }
            })
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'EVENT') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'NAME':
                                    columns.push({
                                        label: 'name',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'localization__name',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by =
                                        preference.ordering_asc !== null
                                            ? 'localization__name'
                                            : order_by;
                                    break;
                                case 'EVENT_URL':
                                    columns.push({
                                        label: 'event-url',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'sub_domain',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by =
                                        preference.ordering_asc !== null
                                            ? 'sub_domain'
                                            : order_by;
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({
                                        label: 'created-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'created_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by =
                                        preference.ordering_asc !== null
                                            ? 'created_date'
                                            : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({
                                        label: 'created-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'creator__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by =
                                        preference.ordering_asc !== null
                                            ? 'creator__username'
                                            : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({
                                        label: 'modified-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'modified_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by =
                                        preference.ordering_asc !== null
                                            ? 'modified_date'
                                            : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({
                                        label: 'modified-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'last_author__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by =
                                        preference.ordering_asc !== null
                                            ? 'last_author__username'
                                            : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order =
                                preference.ordering_asc !== null
                                    ? preference.ordering_asc
                                        ? 'asc'
                                        : 'desc'
                                    : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map((column) => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({ ...column });
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            .addDefaultCase((state, action) => { });
    }
});

export const {
    addActivationDate,
    addPreferences,
    deleteActivationDate,
    deletePicture,
    moveDownColumn,
    moveUpColumn,
    resetEvent,
    resetFilters,
    setEdit,
    setEditStep,
    setEvent,
    setEventActivationDate,
    setEventActivationDateDelete,
    setErrors,
    setEventPreferences,
    setExportType,
    setLimit,
    setList,
    setLogoErrors,
    setOrderBy,
    setPage,
    setRunning,
    setSearch,
    toggleAllowDeposit,
    toggleBankTransferActivated,
    toggleOpentoBTC,
    toggleNoInvoiceOnCbPayment,
    toggleCartAMWC,
    toggleColumn,
    toggleCreate,
    toggleEditActivatedAccommodations,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    toggleHasPassword,
    toggleRefresh,
    toggleSilentSignIn,
    toggleTravelerCanAdd,
    toggleTravelerCanDelete,
    updateActivatedAccommodations,
    updateActivatedModule,
    updateActivationBankTransferLimitDate,
    updateActivationCancellationLimitDate,
    updateActivationDepositLimitDate,
    updateActivationPaxUpdateLimitDate,
    updateActivationEndDate,
    updateActivationEventEndDate,
    updateActivationEventStartDate,
    updateActivationName,
    updateActivationNbDaysAllowedAfterEvent,
    updateActivationNbDaysAllowedBeforeEvent,
    updateActivationStartDate,
    updateAddress,
    updateAddressSearch,
    updateBackgroundColor,
    updateButtonColor,
    updateCity,
    updateContactLocale,
    updateDescriptionLocale,
    updateFilterCreatedDateGte,
    updateFilterCreator,
    updateFilterCreatedDateLte,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterName,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateFooterElements,
    updateGoogleId,
    updateInvoicePrefix,
    updateInvoicePrefixError,
    updateLatitude,
    updateLongitude,
    updateLogo,
    updateLogoProgress,
    updatePassword,
    updatePictureOrder,
    updateNameLocale,
    updateSubDomain,
    updateTermsAndConditionsLocale,
    updateTextColor,
    updateTitleLocale,
    updateUnavailableMessageLocale,
    updateZipCode,
    updateMeetingCode,
    updateAllowedTeams,
    updateAllowedUsers,
    updateClientList,
    updateAccountingCode,
    updateAnalyticCode,
    addAnalyticCode,
    deleteAnalyticCode,
    updateAgent,
    setColumns,
    resizeColumn
} = eventSlice.actions;

export default eventSlice.reducer;
